import { LitElement, css, html, nothing } from '@lit'

class NovaCardVideo extends LitElement {
  static properties = {
    videoObject: {
      type: Object,
      attribute: 'video-object',
    },
    autoplay: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    description: {
      type: String,
    },
  }

  get videoElement() {
    return this.renderRoot?.querySelector('video') ?? null
  }

  get posterImage() {
    const previewImage = this.videoObject?.preview_image
    if (typeof previewImage === 'string') return previewImage
    if (previewImage?.src) return previewImage.src
    return this.videoObject?.poster_image ?? null
  }

  constructor() {
    super()
    this.observer = null
    this.mobileViewportQuery = null
    this.hasHoverListeners = false
  }

  _syncAspectRatio() {
    const aspectRatio = this.videoObject?.aspect_ratio
    if (aspectRatio) {
      this.style.setProperty('--nova-aspect-ratio', String(aspectRatio))
    } else {
      this.style.removeProperty('--nova-aspect-ratio')
    }
  }

  _nudgePlaybackAfterSourceChange = () => {
    if (!this.isConnected) return
    if (!this.videoElement) return

    this._syncObserverState()

    if (this.mobileViewportQuery?.matches) {
      this.observer?.unobserve(this)
      this.observer?.observe(this)
    } else if (!this.autoplay && this.matches(':hover')) {
      this._playVideo(this.videoElement)
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('videoObject')) {
      this._syncAspectRatio()

      const video = this.videoElement
      if (video) {
        video.addEventListener('loadeddata', this._nudgePlaybackAfterSourceChange, {
          once: true,
        })
        video.load()
      } else {
        requestAnimationFrame(() => {
          this._nudgePlaybackAfterSourceChange()
        })
      }
    }
  }

  _playVideo(video) {
    const playback = video?.play?.()
    if (playback?.catch) {
      playback.catch(() => {})
    }
  }

  _addHoverListeners() {
    if (this.autoplay || this.hasHoverListeners) return
    this.addEventListener('mouseover', this._handleMouseover)
    this.addEventListener('mouseout', this._handleMouseout)
    this.hasHoverListeners = true
  }

  _removeHoverListeners() {
    if (!this.hasHoverListeners) return
    this.removeEventListener('mouseover', this._handleMouseover)
    this.removeEventListener('mouseout', this._handleMouseout)
    this.hasHoverListeners = false
  }

  _createObserver() {
    if (this.observer) return

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target.renderRoot?.querySelector('video')
        if (!video) return

        if (entry.isIntersecting) {
          this._playVideo(video)
        } else {
          video.pause()
        }
      })
    }, {
      root: null,
      rootMargin: '-10% 0px -10% 0px',
      threshold: 0.5,
    })
  }

  _syncObserverState = () => {
    if (!this.mobileViewportQuery?.matches) {
      this.observer?.disconnect()
      if (this.autoplay) {
        this._removeHoverListeners()
        this._playVideo(this.videoElement)
      } else {
        this.videoElement?.pause()
        this._addHoverListeners()
      }
      return
    }

    this._removeHoverListeners()
    this._createObserver()
    this.observer?.observe(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.mobileViewportQuery = window.matchMedia('(max-width: 1199px)')
    if (this.mobileViewportQuery.addEventListener) {
      this.mobileViewportQuery.addEventListener('change', this._syncObserverState)
    } else {
      this.mobileViewportQuery.addListener(this._syncObserverState)
    }
    this._syncObserverState()
    this._syncAspectRatio()
  }

  _handleMouseover = () => {
    this._playVideo(this.videoElement)
  }

  _handleMouseout = () => {
    this.videoElement?.pause()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this.mobileViewportQuery?.removeEventListener) {
      this.mobileViewportQuery.removeEventListener('change', this._syncObserverState)
    } else {
      this.mobileViewportQuery?.removeListener(this._syncObserverState)
    }
    this.observer?.disconnect()
    this._removeHoverListeners()
  }

  render() {
    return html`
      <video
        playsinline
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        preload="none"
        muted
        aria-label=${this.description}
        poster=${this.posterImage ?? nothing}
      >
        ${this.videoObject?.sources?.map(
          (source) =>
            html`<source src=${source.url} type=${source.mime_type}></source>`,
        )}
      </video>
    `
  }

  static styles = css`
    :host {
      aspect-ratio: var(--nova-aspect-ratio, 16 / 9);
      display: block;
      position: relative;
    }
    video {
      background: black;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
    }
  `
}

window.customElements.define('nova-card-video', NovaCardVideo)
